'use client'

import { sendGTMEvent } from '@next/third-parties/google'
import { type SearchResults } from 'algoliasearch-helper'
import { useEffect } from 'react'

import { type AlgoliaRecord } from '../../algolia'
import { SHOP_APOTHEKE_SELLER_ID } from '../../seller'

import { productSearchGetOwnBrandHits } from './productSearchGetOwnBrandHits'
import {
  SERP_POSITION_FIFTEEN,
  SERP_POSITION_FIVE,
} from './productSearchSerpPositions'

type ActiveFilter = {
  filter: string
  filterName: string
  hits: number
}

const setDisjunctiveFilter = (
  refinement: SearchResults.Refinement,
  activeFilters: ActiveFilter[],
): void => {
  const { attributeName, count, name } = refinement

  activeFilters.push({
    filter: name,
    filterName: attributeName,
    hits: count,
  })
}

const setRangeFilter = (
  refinement: SearchResults.Refinement,
  activeFilters: ActiveFilter[],
  totalCount: number,
): void => {
  const { attributeName, numericValue, operator } = refinement

  const activeFilter = activeFilters.find(
    (filter) => filter.filterName === attributeName,
  )

  if (activeFilter) {
    const filter = String(numericValue)

    if (operator === '>=') {
      activeFilter.filter = `${filter}${activeFilter.filter}`
    } else if (operator === '<=') {
      activeFilter.filter = `${activeFilter.filter}${filter}`
    }
  } else {
    let filter = String(numericValue)

    if (operator === '>=') {
      filter += `-`
    } else if (operator === '<=') {
      filter = `-${filter}`
    }

    activeFilters.push({
      filter,
      filterName: attributeName,

      /**
       * Very strange logic from Fock to pass total hits amount for range filter
       * https://github.com/RedTecLab/fock/blob/cb77fa3d59c5863f995c56bf60f9a083b9c20c6f/app/shared/utils/gtmUtils.ts#L91.
       * Keep it as it is for backward compatibility.
       */
      hits: totalCount,
    })
  }
}

const getActiveFilters = (
  refinements: SearchResults.Refinement[],
  totalCount: number,
): ActiveFilter[] => {
  const activeFilters: ActiveFilter[] = []

  /* eslint-disable unicorn/no-break-in-nested-loop -- don't reference, refactor if touched */
  for (const refinement of refinements) {
    const { type } = refinement

    switch (type) {
      case 'disjunctive': {
        setDisjunctiveFilter(refinement, activeFilters)
        break
      }

      case 'numeric': {
        setRangeFilter(refinement, activeFilters, totalCount)
        break
      }

      default: {
        break
      }
    }
  }
  /* eslint-enable unicorn/no-break-in-nested-loop */

  return activeFilters
}

type MarketplaceHits = {
  mpHits: number
  mpTop15?: number
  mpTop30?: number
  mpTop5?: number
}

const getMarketplaceHits = (
  hits: AlgoliaRecord[],
  pageNumber: number,
): MarketplaceHits => {
  let mpHits = 0
  let mpTop5 = 0
  let mpTop15 = 0
  const isFirstPage = pageNumber === 1

  for (const [index, hit] of hits.entries()) {
    const sellerId = hit.best_offer?.seller?.id

    if (sellerId !== undefined && sellerId !== SHOP_APOTHEKE_SELLER_ID) {
      mpHits += 1

      if (isFirstPage && index < SERP_POSITION_FIVE) {
        mpTop5 += 1
      }

      if (isFirstPage && index < SERP_POSITION_FIFTEEN) {
        mpTop15 += 1
      }
    }
  }

  return isFirstPage ? { mpHits, mpTop15, mpTop30: mpHits, mpTop5 } : { mpHits }
}

export const useProductSearchResultsGtmTracker = (
  results: SearchResults<AlgoliaRecord> | undefined,
): void => {
  useEffect(() => {
    if (!results) {
      return
    }

    const activeFilters = getActiveFilters(
      results.getRefinements(),
      results.nbHits,
    )

    const { hits, hitsPerPage, nbHits, page, query } = results
    const visualPageNumber = page + 1

    const ownBrandHits = productSearchGetOwnBrandHits(hits, visualPageNumber)

    sendGTMEvent({
      algolia: {
        activeFilters,
        marketplaceHits: getMarketplaceHits(hits, visualPageNumber),
        ownBrandHits,
        pageNumber: visualPageNumber,
        pageSize: hitsPerPage,
        query,
        totalCount: nbHits,
      },
      filterData: activeFilters,
    })
  }, [results])
}
